import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const KNOWN = ["company", "email", "phone", "count", "participants"];
const FALLBACK_CFG = { title: "Zgłoszenie udziału", description: "", fields: [] };

export function EventRegisterDialog({ training, onClose, onSuccess }) {
  const [cfg, setCfg] = useState(FALLBACK_CFG);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/event-reg-form").then((r) => setCfg(r.data || FALLBACK_CFG)).catch(() => setCfg(FALLBACK_CFG));
  }, []);
  useEffect(() => { if (training) setValues({}); }, [training]);

  const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));
  const fields = cfg?.fields || [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { company: "", email: "", phone: "", count: 1, participants: "", answers: {} };
      fields.forEach((f) => {
        const v = values[f.id] ?? "";
        if (KNOWN.includes(f.id)) payload[f.id] = f.id === "count" ? (Number(v) || 1) : v;
        else payload.answers[f.id] = { label: f.label, value: v };
      });
      await api.post(`/trainings/${training.id}/register`, payload);
      toast.success("Zgłoszenie wysłane! Skontaktujemy się mailowo.");
      onSuccess?.();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!training} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="register-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{cfg?.title || "Zgłoszenie udziału"}{training ? `: ${training.title}` : ""}</DialogTitle>
        </DialogHeader>
        {cfg?.description && <p className="text-sm text-muted-foreground">{cfg.description}</p>}
        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => (
            <div key={f.id}>
              <Label>{f.label}{f.required ? " *" : ""}</Label>
              {f.type === "textarea" ? (
                <Textarea rows={3} required={f.required} value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} data-testid={`reg-${f.id}`} className="mt-1.5" />
              ) : f.type === "select" ? (
                <select required={f.required} value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} data-testid={`reg-${f.id}`} className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">— wybierz —</option>
                  {(f.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"} required={f.required} value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} data-testid={`reg-${f.id}`} className={`mt-1.5 ${f.type === "number" ? "w-32" : ""}`} />
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-ztk-blue" data-testid="reg-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Wyślij zgłoszenie
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
